import { useCallback, useEffect, useState } from 'react';
import { Award, History, Info } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import awardService from '../../services/awards';
import type {
  AwardCode, AwardReference, AwardStream, EmploymentBasis, StaffAwardPerson,
} from '../../types/awards';
import {
  BASIS_LABEL, KIND_TITLE, NOT_ADVICE, STREAM_LABEL, awardName, levelLabel,
} from '../../utils/awardReviews';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import AwardReviewItem from './AwardReviewItem';

/**
 * The staff profile's Award tab (Timesheet HR seat): which award and level
 * the person is on and since when, the dates the reminders count from, and
 * the reminders themselves. A level is never edited: a change is a new row
 * from a date, so the history says who was on what in any month.
 */

function errText(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
  return e?.response?.data?.error?.message || e?.message || fallback;
}

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500';

interface ProfileDraft {
  date_of_birth: string;
  employment_start_date: string;
  industry_experience_from: string;
  employment_basis: EmploymentBasis | '';
  stream: AwardStream;
}

export default function AwardPanel({ userId, userName }: { userId: number; userName: string }) {
  const orgTz = useOrgTimezone();
  const fmt = useCallback((d: string) => formatLocalDateInTz(d, orgTz), [orgTz]);
  const [ref, setRef] = useState<AwardReference | null>(null);
  const [person, setPerson] = useState<StaffAwardPerson | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  // The level form: open, and the reminder it answers if opened from one.
  const [classifying, setClassifying] = useState<{ promptId: number | null } | null>(null);
  const [cls, setCls] = useState<{ award_code: AwardCode | ''; level_key: string; effective_from: string; reason: string }>(
    { award_code: '', level_key: '', effective_from: '', reason: '' });

  const take = useCallback((p: StaffAwardPerson) => {
    setPerson(p);
    setDraft({
      date_of_birth: p.profile?.date_of_birth || '',
      employment_start_date: p.profile?.employment_start_date || '',
      industry_experience_from: p.profile?.industry_experience_from || '',
      employment_basis: p.profile?.employment_basis || '',
      stream: p.profile?.stream || 'standard',
    });
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [r, p] = await Promise.all([awardService.reference(), awardService.person(userId)]);
      if (r.success && r.data) setRef(r.data);
      if (p.success && p.data) take(p.data);
      else setError('Could not load this person\'s award details.');
    } catch (err) {
      setError(errText(err, 'Could not load this person\'s award details.'));
    }
  }, [userId, take]);

  useEffect(() => {
    setPerson(null);
    setClassifying(null);
    setNotice(null);
    load();
  }, [load]);

  const run = async (fn: () => Promise<{ success: boolean; data?: StaffAwardPerson; error?: { message?: string } }>, done: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fn();
      if (!res.success || !res.data) { setError(res.error?.message || 'That did not save. Try again.'); return false; }
      take(res.data);
      setNotice(done);
      return true;
    } catch (err) {
      setError(errText(err, 'That did not save. Try again.'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openClassify = (promptId: number | null) => {
    if (!person) return;
    const code = (person.current?.award_code || person.award_code || ref?.awards[0]?.code || '') as AwardCode | '';
    const prompt = promptId ? person.prompts.find(p => p.id === promptId) : null;
    setCls({
      award_code: code,
      level_key: prompt?.detail.to_level || prompt?.detail.highest_level || '',
      effective_from: prompt && prompt.kind === 'time_in_level' && prompt.due_on > person.today ? prompt.due_on : person.today,
      reason: prompt ? KIND_TITLE[prompt.kind] : '',
    });
    setClassifying({ promptId });
  };

  const saveClassification = async () => {
    if (!cls.award_code || !cls.level_key || !cls.effective_from) return;
    const ok = await run(() => awardService.classify(userId, {
      award_code: cls.award_code as AwardCode,
      level_key: cls.level_key,
      effective_from: cls.effective_from,
      reason: cls.reason || null,
      prompt_id: classifying?.promptId ?? null,
    }), 'Classification recorded.');
    if (ok) setClassifying(null);
  };

  const saveProfile = () => {
    if (!draft) return;
    run(() => awardService.saveProfile(userId, {
      date_of_birth: draft.date_of_birth || null,
      employment_start_date: draft.employment_start_date || null,
      industry_experience_from: draft.industry_experience_from || null,
      employment_basis: draft.employment_basis || null,
      stream: draft.stream,
    }), 'Details saved.');
  };

  const resolve = async (id: number, outcome: 'no_change' | 'dismissed', note: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await awardService.resolve(id, outcome, note);
      if (!res.success) { setError(res.error?.message || 'That did not save.'); return; }
      const p = await awardService.person(userId);
      if (p.success && p.data) take(p.data);
    } catch (err) {
      setError(errText(err, 'That did not save.'));
    } finally {
      setBusy(false);
    }
  };

  if (!person || !draft) {
    return (
      <Card><CardContent className="pt-6">
        {error ? <p className="text-sm text-red-600">{error}</p> : <p className="text-sm text-neutral-500">Loading…</p>}
      </CardContent></Card>
    );
  }

  const selectedAward = ref?.awards.find(a => a.code === cls.award_code) || null;
  const selectedLevel = selectedAward?.levels.find(l => l.key === cls.level_key) || null;
  const open = person.prompts.filter(p => p.status === 'open');
  const closed = person.prompts.filter(p => p.status !== 'open');
  const currentLevel = person.current ? selectedLevelSummary(ref, person.current.award_code, person.current.level_key) : null;

  return (
    <div className="space-y-6">
      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 break-words">{error}</div>}
      {notice && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">{notice}</div>}

      {/* Award and level */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-start gap-3">
            <Award className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-neutral-900">Award and classification</h3>
              {person.current ? (
                <>
                  <p className="text-sm text-neutral-800 break-words">
                    <span className="font-medium">{levelLabel(ref, person.current.award_code, person.current.level_key)}</span>
                    {', '}{awardName(ref, person.current.award_code)}, since {fmt(person.current.effective_from)}
                  </p>
                  {currentLevel && <p className="text-xs text-neutral-500 break-words mt-1">{currentLevel}</p>}
                </>
              ) : (
                <p className="text-sm text-neutral-600">
                  No award recorded for {userName}. Not every role is covered by an award; record one for anyone who is.
                </p>
              )}
              {person.upcoming.map(u => (
                <p key={u.id} className="text-sm text-primary-800 mt-1 break-words">
                  From {fmt(u.effective_from)}: {levelLabel(ref, u.award_code, u.level_key)}, {awardName(ref, u.award_code)}
                </p>
              ))}
            </div>
            {!classifying && (
              <Button size="sm" onClick={() => openClassify(null)} className="shrink-0">
                {person.current ? 'Change level' : 'Record award'}
              </Button>
            )}
          </div>

          {classifying && (
            <div className="border border-primary-200 bg-primary-50/40 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="award-code" className="block text-xs text-neutral-600 mb-1">Award</label>
                  <select
                    id="award-code"
                    className={inputCls}
                    value={cls.award_code}
                    onChange={e => setCls(c => ({ ...c, award_code: e.target.value as AwardCode, level_key: '' }))}
                  >
                    <option value="">Choose an award</option>
                    {ref?.awards.map(a => <option key={a.code} value={a.code}>{a.name} ({a.code})</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="award-level" className="block text-xs text-neutral-600 mb-1">Level</label>
                  <select
                    id="award-level"
                    className={inputCls}
                    value={cls.level_key}
                    disabled={!selectedAward}
                    onChange={e => setCls(c => ({ ...c, level_key: e.target.value }))}
                  >
                    <option value="">Choose a level</option>
                    {selectedAward?.levels.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="award-from" className="block text-xs text-neutral-600 mb-1">From</label>
                  <input
                    id="award-from"
                    type="date"
                    className={inputCls}
                    value={cls.effective_from}
                    onChange={e => setCls(c => ({ ...c, effective_from: e.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="award-reason" className="block text-xs text-neutral-600 mb-1">Reason</label>
                  <input
                    id="award-reason"
                    className={inputCls}
                    value={cls.reason}
                    maxLength={1000}
                    placeholder="e.g. Forklift licence, now drives it daily"
                    onChange={e => setCls(c => ({ ...c, reason: e.target.value }))}
                  />
                </div>
              </div>
              {selectedLevel && <p className="text-xs text-neutral-600 break-words">{selectedLevel.summary}</p>}
              <p className="text-xs text-neutral-500">
                A level is never edited. This adds a new entry from the date chosen; earlier entries stay on the record.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" disabled={busy || !cls.award_code || !cls.level_key || !cls.effective_from} onClick={saveClassification}>
                  {busy ? 'Saving…' : 'Record'}
                </Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => setClassifying(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reminders for this person */}
      {(open.length > 0 || closed.length > 0) && (
        <Card>
          <CardContent className="pt-6 space-y-3">
            <h3 className="text-base font-semibold text-neutral-900">Pay review reminders</h3>
            <ul className="space-y-2">
              {open.map(r => (
                <AwardReviewItem
                  key={r.id}
                  review={r}
                  reference={ref}
                  today={person.today}
                  formatDate={fmt}
                  busy={busy}
                  onResolve={(outcome, note) => resolve(r.id, outcome, note)}
                  onReclassify={() => openClassify(r.id)}
                />
              ))}
              {closed.slice(0, 5).map(r => (
                <AwardReviewItem key={r.id} review={r} reference={ref} today={person.today} formatDate={fmt} />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* What the reminders count from */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <h3 className="text-base font-semibold text-neutral-900">Dates the reminders count from</h3>
            <p className="text-xs text-neutral-500 mt-1">
              Only people with a Timesheet HR seat can see these. The date of birth is used for the junior rate brackets and goes nowhere else.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="award-dob" className="block text-xs text-neutral-600 mb-1">Date of birth</label>
              <input id="award-dob" type="date" className={inputCls} value={draft.date_of_birth}
                onChange={e => setDraft(d => d && ({ ...d, date_of_birth: e.target.value }))} />
            </div>
            <div>
              <label htmlFor="award-basis" className="block text-xs text-neutral-600 mb-1">Employment</label>
              <select id="award-basis" className={inputCls} value={draft.employment_basis}
                onChange={e => setDraft(d => d && ({ ...d, employment_basis: e.target.value as EmploymentBasis | '' }))}>
                <option value="">Not set</option>
                {(ref?.bases || ['full_time', 'part_time', 'casual']).map(b => <option key={b} value={b}>{BASIS_LABEL[b]}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="award-start" className="block text-xs text-neutral-600 mb-1">Started with you</label>
              <input id="award-start" type="date" className={inputCls} value={draft.employment_start_date}
                onChange={e => setDraft(d => d && ({ ...d, employment_start_date: e.target.value }))} />
            </div>
            <div>
              <label htmlFor="award-exp" className="block text-xs text-neutral-600 mb-1">Industry experience from</label>
              <input id="award-exp" type="date" className={inputCls} value={draft.industry_experience_from}
                onChange={e => setDraft(d => d && ({ ...d, industry_experience_from: e.target.value }))} />
              <p className="text-xs text-neutral-500 mt-1 break-words">
                Experience with earlier employers counts towards the first three months. Leave blank to count from the start date.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="award-stream" className="block text-xs text-neutral-600 mb-1">Pay scale</label>
              <select id="award-stream" className={inputCls} value={draft.stream}
                onChange={e => setDraft(d => d && ({ ...d, stream: e.target.value as AwardStream }))}>
                {(ref?.streams || ['standard']).map(s => <option key={s} value={s}>{STREAM_LABEL[s] || s}</option>)}
              </select>
              {draft.stream !== 'standard' && (
                <p className="text-xs text-neutral-500 mt-1 break-words">
                  Apprentices, trainees and supported wage have their own scales. No age or three-month reminders are raised for them.
                </p>
              )}
            </div>
          </div>

          <StandingLines person={person} reference={ref} fmt={fmt} />

          <Button size="sm" disabled={busy} onClick={saveProfile}>{busy ? 'Saving…' : 'Save details'}</Button>
        </CardContent>
      </Card>

      {/* History */}
      {person.history.length > 0 && (
        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-neutral-500" />
              <h3 className="text-base font-semibold text-neutral-900">Classification history</h3>
            </div>
            <ul className="divide-y divide-neutral-100">
              {person.history.map(h => (
                <li key={h.id} className="py-2 text-sm">
                  <div className="flex flex-wrap gap-x-2">
                    <span className="font-medium text-neutral-900">{fmt(h.effective_from)}</span>
                    <span className="text-neutral-800 break-words min-w-0">
                      {levelLabel(ref, h.award_code, h.level_key)}, {awardName(ref, h.award_code)}
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500 break-words">
                    {h.reason ? `${h.reason}. ` : ''}Recorded{h.recorded_by_name ? ` by ${h.recorded_by_name}` : ''} on {fmt(h.recorded_at.slice(0, 10))}
                  </p>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <p className="flex items-start gap-2 text-xs text-neutral-500 break-words">
        <Info className="h-4 w-4 shrink-0" />
        <span>{NOT_ADVICE} No pay rate is kept here; rates stay in your payroll.</span>
      </p>
    </div>
  );
}

function selectedLevelSummary(ref: AwardReference | null, code: string, key: string): string | null {
  return ref?.awards.find(a => a.code === code)?.levels.find(l => l.key === key)?.summary || null;
}

/** What the saved dates mean today: junior percentage and the three-month date. */
function StandingLines({ person, reference, fmt }: {
  person: StaffAwardPerson; reference: AwardReference | null; fmt: (d: string) => string;
}) {
  const lines: string[] = [];
  const j = person.junior;
  if (j) {
    if (j.next) {
      lines.push(`${j.age} years old: the junior rate is ${j.pct}% of the adult rate until ${fmt(j.next.on)}, when it becomes ${j.next.pct}%.`);
    } else if (j.age < 21) {
      lines.push(`${j.age} years old: paid the adult rate for their level.`);
    }
  } else if (person.award_code && !person.profile?.date_of_birth && (person.profile?.stream || 'standard') === 'standard') {
    lines.push('No date of birth: the junior rate brackets cannot be checked.');
  }
  const t = person.time_rule;
  if (t) {
    const from = levelLabel(reference, person.current?.award_code, t.from);
    const to = levelLabel(reference, person.current?.award_code, t.to);
    lines.push(t.due_on
      ? `${from} to ${to} after ${t.months} months' industry experience: ${fmt(t.due_on)}, counted from ${t.counted_from_kind === 'industry_experience' ? 'industry experience' : 'the start date'}.`
      : `${from} to ${to} after ${t.months} months' industry experience: add a start date or an industry experience date so it can be counted.`);
  }
  if (!lines.length) return null;
  return (
    <ul className="text-sm text-neutral-700 space-y-1 bg-neutral-50 border border-neutral-200 rounded-lg p-3">
      {lines.map(l => <li key={l} className="break-words">{l}</li>)}
    </ul>
  );
}
